import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import { getVcpItems, addVcpTicker, createOrder, getAccount, getAccountTickers, getAccountOrders, promoteVcpToReadylist, demoteVcpToBasing } from '../services/api';
import './Leaders.css';
import { Form } from 'react-bootstrap';
import OrderDialog from '../components/OrderDialog';
import { getAllAccounts } from '../services/api';
import { updateVcpItem } from '../services/api';
import { ThemeContext } from '../contexts/ThemeContext';
import TickerNotesPanel from '../components/TickerNotesPanel';
import VcpRow from '../components/VcpRow';
import VcpEditorModal from '../components/VcpEditorModal';
import { getVcpDisplay } from '../utils/vcpFormat';
import { MAX_VCP_LIST_SIZE } from '../utils/listLimits';

const VCP = () => {
  const {
    effectiveTheme,
    weatherThemeEnabled,
    selectedWeatherTheme,
  } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';
  
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tickerQuery, setTickerQuery] = useState('');
  const [addError, setAddError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [removingRowIds, setRemovingRowIds] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderTicker, setOrderTicker] = useState(null);
  const [orderAccountId, setOrderAccountId] = useState('');
  const [orderDialogLoading, setOrderDialogLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountTickers, setSelectedAccountTickers] = useState([]);
  const [selectedAccountOrders, setSelectedAccountOrders] = useState([]);

  const [sortColumn, setSortColumn] = useState(() => {
    const saved = localStorage.getItem('vcp_sortColumn');
    return saved || 'status';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const saved = localStorage.getItem('vcp_sortDirection');
    return saved || 'asc';
  });
  // Error states
  const [promoteError, setPromoteError] = useState('');
  const [priceUpdateError, setPriceUpdateError] = useState('');
  const [globalPriceUpdateEnabled, setGlobalPriceUpdateEnabled] = useState(false);
  const [globalPriceUpdateLoading, setGlobalPriceUpdateLoading] = useState(false);
  const [vcpEditorOpen, setVcpEditorOpen] = useState(false);
  const [editingVcpItem, setEditingVcpItem] = useState(null);

  const maxLeadersSize = MAX_VCP_LIST_SIZE;
  const removalTimersRef = useRef({});
  const ROW_REMOVE_ANIMATION_DURATION = 260;
  const [pinnedTickerId, setPinnedTickerId] = useState(null);

  const focusTickerBySymbol = (symbol, sourceItems = leaders) => {
    if (!symbol) return;
    const normalized = symbol.trim().toUpperCase();
    const target = sourceItems.find(item => item.ticker?.toUpperCase() === normalized);
    if (target) {
      setPinnedTickerId(target.id);
      setSelectedId(target.id);
      setHighlightedRowId(target.id);
    }
  };

  const dispatchVcpBadge = useCallback((count) => {
    try { localStorage.setItem('vcp_count', String(count)); } catch (e) {}
    window.dispatchEvent(new CustomEvent('vcp:badge', { detail: { count } }));
  }, []);

  const fetchLeaders = async ({ focusTicker } = {}) => {
    setLoading(true);
    setPriceUpdateError('');
    try {
      const res = await getVcpItems();
      let items = res.data || [];

      const activeHoldingsNeedingUpdate = items.filter(
        item => item.is_purchased && item.include_in_price_updates === false
      );

      if (activeHoldingsNeedingUpdate.length > 0) {
        try {
          await Promise.all(
            activeHoldingsNeedingUpdate.map(item =>
              updateVcpItem(item.id, { include_in_price_updates: true })
            )
          );
        } catch (enforceError) {
          console.error('Error enforcing price updates for active holdings:', enforceError);
          setPriceUpdateError(
            enforceError.response?.data?.error ||
            'Failed to enforce price updates for active holdings.'
          );
        } finally {
          items = items.map(item =>
            item.is_purchased ? { ...item, include_in_price_updates: true } : item
          );
        }
      } else {
        items = items.map(item =>
          item.is_purchased ? { ...item, include_in_price_updates: true } : item
        );
      }

      setLeaders(items);
      dispatchVcpBadge(items.length);
      if (focusTicker) {
        focusTickerBySymbol(focusTicker, items);
      } else if (items.length > 0 && !selectedId) {
        setSelectedId(items[0].id);
      }
    } catch (err) {
      setLeaders([]);
      dispatchVcpBadge(0);
      setPriceUpdateError(err.response?.data?.error || 'Failed to load VCP data.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLeaders();
    getAllAccounts().then(res => setAccounts(res.data)).catch(() => setAccounts([]));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const timers = removalTimersRef.current;
    return () => {
      Object.values(timers).forEach(timerId => clearTimeout(timerId));
    };
  }, []);


  useEffect(() => {
    const nonActive = leaders.filter(item => !item.is_purchased);
    if (nonActive.length === 0) {
      setGlobalPriceUpdateEnabled(false);
    } else {
      setGlobalPriceUpdateEnabled(nonActive.every(item => item.include_in_price_updates !== false));
    }
  }, [leaders]);

  // Update ranks when watchlist changes (preserve user edits if possible)
  // Remove useEffect for ranks

  // Helper to get rank for a ticker id
  // Remove getRank

  // Handle rank change
  // Remove handleRankChange

  // Handle rank input blur/enter
  // Remove handleRankInput

  const handleAddTicker = async (e) => {
    e.preventDefault();
    if (leaders.length >= maxLeadersSize) {
              setAddError(`You cannot have more than ${maxLeadersSize} tickers in your VCP list.`);
      return;
    }
    const normalizedTicker = tickerQuery.trim().toUpperCase();
    if (!normalizedTicker) return;
    setAddError('');
    try {
      await addVcpTicker(normalizedTicker);
      fetchLeaders({ focusTicker: normalizedTicker });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to add ticker';
      setAddError(errorMessage);
      if (errorMessage === 'Ticker already in VCP') {
        focusTickerBySymbol(normalizedTicker);
      }
    }
  };

  const handlePromote = async (id) => {
    const tickerToPromote = leaders.find(item => item.id === id);
    if (!tickerToPromote) return;

    const previousLeaders = leaders;
    const wasSelected = selectedId === id;

    setDeletingId(id);
    setPromoteError('');
    setRemovingRowIds(prev =>
      prev.includes(id) ? prev : [...prev, id]
    );

    const finalizeRemoval = () => {
      setLeaders(prev => prev.filter(item => item.id !== id));
      if (wasSelected) {
        setSelectedId(null);
      }
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(current => (current === id ? null : current));
    };

    removalTimersRef.current[id] = setTimeout(() => {
      finalizeRemoval();
      delete removalTimersRef.current[id];
    }, ROW_REMOVE_ANIMATION_DURATION);

    try {
      await promoteVcpToReadylist(id);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to promote ticker';
      const alreadyInWatchlist = errorMessage.toLowerCase().includes('already') &&
        errorMessage.toLowerCase().includes('watchlist');
      if (alreadyInWatchlist) {
        return;
      }
      console.error('Error promoting ticker:', err);
      clearTimeout(removalTimersRef.current[id]);
      delete removalTimersRef.current[id];
      setLeaders(previousLeaders);
      if (wasSelected) {
        setSelectedId(id);
      }
      setPromoteError(errorMessage);
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDemoteToBasing = async (id) => {
    const tickerToDemote = leaders.find(item => item.id === id);
    if (!tickerToDemote) return;

    const previousLeaders = leaders;
    const wasSelected = selectedId === id;

    setDeletingId(id);
    setPromoteError('');
    setRemovingRowIds(prev =>
      prev.includes(id) ? prev : [...prev, id]
    );

    const finalizeRemoval = () => {
      setLeaders(prev => prev.filter(item => item.id !== id));
      if (wasSelected) {
        setSelectedId(null);
      }
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(current => (current === id ? null : current));
    };

    removalTimersRef.current[id] = setTimeout(() => {
      finalizeRemoval();
      delete removalTimersRef.current[id];
    }, ROW_REMOVE_ANIMATION_DURATION);

    try {
      await demoteVcpToBasing(id);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to demote ticker to basing';
      const alreadyInBasing = errorMessage.toLowerCase().includes('already') &&
        errorMessage.toLowerCase().includes('basing');
      if (alreadyInBasing) {
        return;
      }
      console.error('Error demoting ticker to basing:', err);
      clearTimeout(removalTimersRef.current[id]);
      delete removalTimersRef.current[id];
      setLeaders(previousLeaders);
      if (wasSelected) {
        setSelectedId(id);
      }
      setPromoteError(errorMessage);
      setRemovingRowIds(prev => prev.filter(rowId => rowId !== id));
      setHighlightedRowId(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePriceUpdateToggle = async (item, nextValue) => {
    if (item.is_purchased) {
      return;
    }
    if (globalPriceUpdateLoading) {
      return;
    }
    setPriceUpdateError('');
    const previousValue = item.include_in_price_updates !== false;

    setLeaders(prev => prev.map(existing => (
      existing.id === item.id ? { ...existing, include_in_price_updates: nextValue } : existing
    )));

    try {
      const response = await updateVcpItem(item.id, { include_in_price_updates: nextValue });
      const updatedItem = response.data;
      setLeaders(prev => prev.map(existing => (
        existing.id === item.id
          ? (updatedItem.is_purchased
            ? { ...existing, ...updatedItem, include_in_price_updates: true }
            : { ...existing, ...updatedItem })
          : existing
      )));
    } catch (error) {
      console.error('Error updating price update setting:', error);
      setLeaders(prev => prev.map(existing => (
        existing.id === item.id ? { ...existing, include_in_price_updates: previousValue } : existing
      )));
      setPriceUpdateError(error.response?.data?.error || 'Failed to update price update setting.');
    }
  };

  const handleGlobalPriceUpdateToggle = async (nextValue) => {
    if (globalPriceUpdateLoading) {
      return;
    }
    setPriceUpdateError('');
    const nonActiveItems = leaders.filter(item => !item.is_purchased);
    if (nonActiveItems.length === 0) {
      return;
    }

    const itemsToUpdate = nonActiveItems.filter(
      item => (item.include_in_price_updates !== false) !== nextValue
    );

    const previousValues = itemsToUpdate.reduce((acc, item) => {
      acc[item.id] = item.include_in_price_updates !== false;
      return acc;
    }, {});

    setGlobalPriceUpdateLoading(true);

    setLeaders(prev => prev.map(existing => (
      existing.is_purchased
        ? { ...existing, include_in_price_updates: true }
        : { ...existing, include_in_price_updates: nextValue }
    )));

    if (itemsToUpdate.length === 0) {
      setGlobalPriceUpdateLoading(false);
      return;
    }

    try {
      const responses = await Promise.all(
        itemsToUpdate.map(async item => {
          const response = await updateVcpItem(item.id, { include_in_price_updates: nextValue });
          return response.data;
        })
      );

      const responseMap = new Map(responses.map(updated => [updated.id, updated]));

      setLeaders(prev => prev.map(existing => {
        const updated = responseMap.get(existing.id);
        if (updated) {
          const merged = { ...existing, ...updated };
          return merged.is_purchased ? { ...merged, include_in_price_updates: true } : merged;
        }
        return existing.is_purchased ? { ...existing, include_in_price_updates: true } : existing;
      }));
    } catch (error) {
      console.error('Error updating price update setting:', error);
      setLeaders(prev => prev.map(existing => {
        if (existing.is_purchased) {
          return { ...existing, include_in_price_updates: true };
        }
        if (Object.prototype.hasOwnProperty.call(previousValues, existing.id)) {
          return { ...existing, include_in_price_updates: previousValues[existing.id] };
        }
        return existing;
      }));
      setPriceUpdateError(error.response?.data?.error || 'Failed to update price update setting.');
    } finally {
      setGlobalPriceUpdateLoading(false);
    }
  };

  // Get seasonal background color with transparency
  const getSeasonalBackground = () => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
    
    const opacity = isDark ? 0.2 : 0.1; // Higher opacity for dark mode
    
    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`; // Green
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`; // Blue
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`; // Orange
      case 'winter':
        return isDark ? 'rgba(248, 249, 250, 0.1)' : 'rgba(108, 117, 125, 0.1)'; // Gray
      default:
        return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
  };

  const handleCategoryUpdate = (updatedItem) => {
    setLeaders((prev) => prev.map((item) => item.id === updatedItem.id ? { ...item, category_id: updatedItem.category_id, category: updatedItem.category } : item));
  };

  const openVcpEditor = (item) => {
    if (!item) {
      return;
    }
    setEditingVcpItem(item);
    setVcpEditorOpen(true);
  };

  const closeVcpEditor = () => {
    setVcpEditorOpen(false);
    setEditingVcpItem(null);
  };

  // Called by VcpEditorModal once the user hits Save; the modal owns its own
  // form state and only asks us to persist + merge the result into `leaders`.
  const handleVcpSave = async (itemId, payload) => {
    const response = await updateVcpItem(itemId, payload);
    const updatedItem = response?.data;
    setLeaders(prev => prev.map(item => (
      item.id === itemId ? { ...item, ...(updatedItem || payload) } : item
    )));
  };

  // Add rank to sort columns
  const handleSort = (column) => {
    let newDirection;
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortColumn(column);
      newDirection = 'asc';
      setSortDirection('asc');
    }

    // Persist to localStorage
    localStorage.setItem('vcp_sortColumn', column);
    localStorage.setItem('vcp_sortDirection', newDirection);
  };

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Apply ticker filter
  const filteredLeaders = tickerQuery
    ? leaders.filter(item => item.ticker.toLowerCase().includes(tickerQuery.toLowerCase()))
    : leaders;

  // Auto-select ticker when filter results in exactly one match
  useEffect(() => {
    if (tickerQuery && filteredLeaders.length === 1) {
      setSelectedId(filteredLeaders[0].id);
    }
  }, [tickerQuery, filteredLeaders]);

  // Sort by rank for display (unless sorting by another column)
  const sortedLeaders = [...filteredLeaders].sort((a, b) => {
    let aValue, bValue;
    switch (sortColumn) {
      case 'ticker':
        aValue = a.ticker?.toUpperCase() || '';
        bValue = b.ticker?.toUpperCase() || '';
        break;
      case 'price':
        aValue = a.price ?? 0;
        bValue = b.price ?? 0;
        break;
      case 'gain':
        aValue = a.gain ?? 0;
        bValue = b.gain ?? 0;
        break;
      case 'gain_pct':
        aValue = a.gain_pct ?? 0;
        bValue = b.gain_pct ?? 0;
        break;
      case 'status':
        aValue = a.is_purchased ? 1 : 0;
        bValue = b.is_purchased ? 1 : 0;
        break;
      case 'category':
        aValue = a.category?.toUpperCase() || '';
        bValue = b.category?.toUpperCase() || '';
        break;
      case 'notes':
        // Sort by latest note update time, nulls last
        if (!a.latest_note_updated_at && !b.latest_note_updated_at) return 0;
        if (!a.latest_note_updated_at) return 1;
        if (!b.latest_note_updated_at) return -1;
        aValue = new Date(a.latest_note_updated_at).getTime();
        bValue = new Date(b.latest_note_updated_at).getTime();
        break;
      case 'vcp':
        aValue = getVcpDisplay(a);
        bValue = getVcpDisplay(b);
        break;
      default:
        // Default to status sorting if no specific column is set
        aValue = a.is_purchased ? 1 : 0;
        bValue = b.is_purchased ? 1 : 0;
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const prioritizedLeaders = useMemo(() => {
    if (!pinnedTickerId) {
      return sortedLeaders;
    }
    const targetIndex = sortedLeaders.findIndex(item => item.id === pinnedTickerId);
    if (targetIndex <= 0) {
      return sortedLeaders;
    }
    const targetItem = sortedLeaders[targetIndex];
    return [
      targetItem,
      ...sortedLeaders.slice(0, targetIndex),
      ...sortedLeaders.slice(targetIndex + 1)
    ];
  }, [sortedLeaders, pinnedTickerId]);

  const handleRowClick = (id) => {
    setSelectedId(id);
  };

  useEffect(() => {
    if (pinnedTickerId && !leaders.some(item => item.id === pinnedTickerId)) {
      setPinnedTickerId(null);
    }
  }, [leaders, pinnedTickerId]);

  const handleTickerClear = () => {
    setTickerQuery('');
    setPinnedTickerId(null);
    setAddError('');
  };



  // Create enhanced accounts array with full account data for the selected account
  const enhancedAccounts = accounts.map(acc => {
    if (acc.account_id === orderAccountId && selectedAccount) {
      return selectedAccount;
    }
    return acc;
  });

  const fetchAccountData = async (accountId) => {
    try {
      // Fetch account details, tickers, and orders
      const [accountRes, tickersRes, ordersRes] = await Promise.all([
        getAccount(accountId),
        getAccountTickers(accountId),
        getAccountOrders(accountId)
      ]);
      
      setSelectedAccount(accountRes.data);
      setSelectedAccountTickers(tickersRes.data);
      setSelectedAccountOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching account data:', error);
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
    }
  };

  const handleAccountChange = async (newAccountId) => {
    setOrderAccountId(newAccountId);
    if (newAccountId) {
      await fetchAccountData(newAccountId);
    } else {
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
    }
  };
  const handleOrderDialogClose = () => {
    setShowOrderDialog(false);
    setOrderTicker(null);
    setOrderAccountId('');
    setSelectedAccount(null);
    setSelectedAccountTickers([]);
    setSelectedAccountOrders([]);
  };
  const handleOrderSave = async (orderData) => {
    setOrderDialogLoading(true);
    try {
      await createOrder(orderAccountId, orderData);
      setShowOrderDialog(false);
      setOrderTicker(null);
      setOrderAccountId('');
      setSelectedAccount(null);
      setSelectedAccountTickers([]);
      setSelectedAccountOrders([]);
      // Optionally refresh data
    } catch (error) {
      console.error('Error creating order:', error);
      // You might want to show an error message to the user here
    } finally {
      setOrderDialogLoading(false);
    }
  };

  // Calculate available cash after existing orders
  const nonDeletedOrderTotal = selectedAccountOrders.filter(o => o.status !== 'DELETED' && o.status !== 'FULFILLED').reduce((sum, o) => sum + (Number(o.price) * Number(o.quantity)), 0);
  const availableCashAfterOrders = (selectedAccount?.cash || 0) - nonDeletedOrderTotal;
  const nonActiveLeadersCount = leaders.filter(item => !item.is_purchased).length;

  // Helper function to get ticker ID for a leader item
  const getTickerIdForLeaderItem = (leaderItem) => {
    // The ticker_id is now included in the leader item data from the backend
    return leaderItem.ticker_id;
  };

  return (
    <div className="container-xl" style={{ '--seasonal-bg': getSeasonalBackground() }}>
      <div className="leaders-2col-layout">
        <div className="leaders-table-col">
          <form className="leaders-add-form" onSubmit={handleAddTicker}>
            <div className="leaders-input-row">
              <div className="leaders-input-stack">
                <div className="leaders-input-wrapper">
                  <input
                    type="text"
                    className="leaders-input"
                    placeholder="Filter or add ticker (e.g. AAPL)"
                    value={tickerQuery}
                    onChange={e => setTickerQuery(e.target.value)}
                    maxLength={16}
                  />
                </div>
                <button
                  type="button"
                  className="leaders-action-btn leaders-clear-btn"
                  onClick={handleTickerClear}
                  disabled={!tickerQuery}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="leaders-action-btn leaders-add-btn"
                  disabled={leaders.length >= maxLeadersSize}
                >
                  Add
                </button>
              </div>
              <div className="leaders-total-summary">
                TOTAL ({prioritizedLeaders.length}/{maxLeadersSize})
              </div>
            </div>
            <Form.Check
              type="switch"
              id="leaders-global-price-toggle"
              className="price-update-toggle"
              checked={globalPriceUpdateEnabled}
              disabled={globalPriceUpdateLoading || nonActiveLeadersCount === 0}
              onChange={e => handleGlobalPriceUpdateToggle(e.target.checked)}
              title={
                nonActiveLeadersCount === 0
                  ? 'All tickers on the VCP list are active holdings and already receive price updates.'
                  : 'Toggle price updates for every non-holding ticker in the VCP list.'
              }
              label={
                <div className="two-line-label">
                  <span>Price updates</span>
                  <span>(non-holdings)</span>
                </div>
              }
            />
            {addError && <span className="leaders-error">{addError}</span>}
            {promoteError && <span className="leaders-error">{promoteError}</span>}
            {priceUpdateError && <span className="leaders-error">{priceUpdateError}</span>}
            {leaders.length >= maxLeadersSize && (
              <span className="leaders-error">Maximum of {maxLeadersSize} tickers reached.</span>
            )}
          </form>
          <div className="tickers-table-container">
            <div className="table-responsive">
              <table className="ticker-table tickers-table" style={{ tableLayout: 'auto', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('ticker')}>
                      Ticker{getSortIndicator('ticker')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '80px' }} onClick={() => handleSort('notes')}>
                      Notes{getSortIndicator('notes')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '120px' }} onClick={() => handleSort('vcp')}>
                      VCP{getSortIndicator('vcp')}
                    </th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('price')}>
                      Price{getSortIndicator('price')}
                    </th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('gain_pct')}>
                      Gain %{getSortIndicator('gain_pct')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer', width: '60px' }} onClick={() => handleSort('status')}>
                      Status{getSortIndicator('status')}
                    </th>
                    <th className="text-center" style={{ width: '90px' }}>Update</th>
                    <th className="text-end" style={{ cursor: 'pointer', width: '172px' }} onClick={() => handleSort('category')}>
                      Category{getSortIndicator('category')}
                    </th>
                    <th className="text-end" style={{ width: '180px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10}>Loading...</td></tr>
                  ) : prioritizedLeaders.length === 0 ? (
                    <tr><td colSpan={10}>No tickers in your VCP list.</td></tr>
                  ) : (
                    <>
                      {prioritizedLeaders.map((item, index) => (
                        <VcpRow
                          key={item.id}
                          item={item}
                          index={index}
                          selected={selectedId === item.id}
                          highlighted={highlightedRowId === item.id}
                          removing={removingRowIds.includes(item.id)}
                          deleting={deletingId === item.id}
                          globalPriceUpdateLoading={globalPriceUpdateLoading}
                          onRowClick={handleRowClick}
                          onEditVcp={openVcpEditor}
                          onPriceUpdateToggle={handlePriceUpdateToggle}
                          onCategoryUpdate={handleCategoryUpdate}
                          onPromote={handlePromote}
                          onDemote={handleDemoteToBasing}
                        />
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <VcpEditorModal
            show={vcpEditorOpen}
            item={editingVcpItem}
            onClose={closeVcpEditor}
            onSave={handleVcpSave}
          />
          {/* Order Dialog Modal */}
          {showOrderDialog && (
            <OrderDialog
              key={`order-dialog-${orderAccountId}`}
              show={showOrderDialog}
              onHide={handleOrderDialogClose}
              onSave={handleOrderSave}
              accounts={enhancedAccounts}
              initialAccountId={orderAccountId}
              setAccountId={handleAccountChange}
              ticker={orderTicker}
              tickers={selectedAccountTickers}
              availableTickers={orderTicker ? [{ symbol: orderTicker }] : []}
              availableCash={availableCashAfterOrders}
              loading={orderDialogLoading}
            />
          )}
        </div>
        <div className="leaders-notes-col">
          <TickerNotesPanel
            selectedItem={leaders.find(w => w.id === selectedId)}
            getTickerIdForItem={getTickerIdForLeaderItem}
            onNoteChange={fetchLeaders}
          />
        </div>
      </div>
    </div>
  );
};

export default VCP; 
